import type { Utility } from '../../types/utility';

/** The Heading variants. */
export type BodyHeadingVariantsType = 'headingS' | 'headingM' | 'headingL' | 'headingXL' | 'heading2XL';
/** The Heading Size variants. */
export type HeadingSizeVariantsType = '3XS' | '2XS' | 'XS' | 'S' | 'M' | 'L' | 'XL' | '2XL' | '3XL' | '4XL' | '5XL';
/** The Heading Size aliases variants. @returns `number` */
export type HeadingSizeAliases = Record<HeadingSizeVariantsType, number>;
/** The Heading Size variants aliases. */
export type HeadingSizesVariantsType = number[] & Partial<Record<HeadingSizeVariantsType, number>>;
export const headingSizes: HeadingSizesVariantsType = [8, 10, 12, 16, 20, 24, 32, 40, 48, 64, 80];

/** The Heading variants object. */
export type HeadingVariantsObjectType = {
  [key in BodyHeadingVariantsType]: Utility.TypographyObject;
};
/** The Heading variants object for all sites. */
export type Heading = HeadingVariantsObjectType & Utility.VariantModes<HeadingVariantsObjectType>;

export const headingAliases: HeadingSizeAliases = {
  /** @returns `8` */
  '3XS': headingSizes[0],
  /** @returns `10` */
  '2XS': headingSizes[1],
  /** @returns `12` */
  XS: headingSizes[2],
  /** @returns `16` */
  S: headingSizes[3],
  /** @returns `20` */
  M: headingSizes[4],
  /** @returns `24` */
  L: headingSizes[5],
  /** @returns `32` */
  XL: headingSizes[6],
  /** @returns `40` */
  '2XL': headingSizes[7],
  /** @returns `48` */
  '3XL': headingSizes[8],
  /** @returns `64` */
  '4XL': headingSizes[9],
  /** @returns `80` */
  '5XL': headingSizes[10],
};

Object.keys(headingAliases).map(headingSize => {
  const baseHeadingSizeKey = headingSize as HeadingSizeVariantsType;
  headingSizes[baseHeadingSizeKey] = headingAliases[baseHeadingSizeKey];
});

export const bodyHeadingStyles: HeadingVariantsObjectType = {
  headingS: {
    color: 'heading',
    fontSize: 'S',
    fontWeight: 'stronger',
    lineHeight: 'default',
    marginTop: 0,
    marginBottom: 3,
  },
  headingM: {
    color: 'heading',
    fontSize: 'M',
    fontWeight: 'stronger',
    lineHeight: 'default',
    marginTop: 0,
    marginBottom: '20px',
  },
  headingL: {
    color: 'heading',
    fontSize: 'L',
    fontWeight: 'stronger',
    lineHeight: 'default',
    marginTop: 0,
    marginBottom: 4,
  },
  headingXL: {
    color: 'heading',
    fontSize: 'XL',
    fontWeight: 'stronger',
    lineHeight: 'default',
    marginTop: 0,
    marginBottom: 5,
  },
  heading2XL: {
    color: 'heading',
    fontSize: ['XL', 'XL', '2XL'],
    fontWeight: 'stronger',
    lineHeight: 'default',
    marginTop: 0,
    marginBottom: 6,
  },
};

type HeroHeadingVariantsType =
  | 'hero2XL700'
  | 'hero2XL900'
  | 'hero2XL900Italic'
  | 'hero3XL700'
  | 'hero3XL900'
  | 'hero3XL900Italic'
  | 'hero4XL700'
  | 'hero4XL900'
  | 'hero4XL900Italic'
  | 'hero5XL700'
  | 'hero5XL900'
  | 'hero5XL900Italic';

type HeroHeadingVariants = Utility.TypographyVariantObjects<HeroHeadingVariantsType, Utility.TypographyObject>;

export const heroHeadingStyles: HeroHeadingVariants = {
  hero2XL700: {
    color: 'heading',
    fontSize: ['XL', 'XL', '2XL'],
    fontWeight: 'stronger',
    lineHeight: 'default',
    marginTop: 0,
    marginBottom: 6,
  },
  hero2XL900: {
    color: 'heading',
    fontSize: ['XL', 'XL', '2XL'],
    fontWeight: 'strongest',
    lineHeight: 'default',
    marginTop: 0,
    marginBottom: 6,
  },
  hero2XL900Italic: {
    color: 'heading',
    fontSize: ['XL', 'XL', '2XL'],
    fontWeight: 'strongest',
    fontStyle: 'italic',
    lineHeight: 'default',
    marginTop: 0,
    marginBottom: 6,
  },
  hero3XL700: {
    color: 'heading',
    fontSize: ['XL', 'XL', '3XL'],
    fontWeight: 'stronger',
    lineHeight: 'default',
    marginTop: 0,
    marginBottom: 6,
  },
  hero3XL900: {
    color: 'heading',
    fontSize: ['XL', 'XL', '3XL'],
    fontWeight: 'strongest',
    lineHeight: 'default',
    marginTop: 0,
    marginBottom: 6,
  },
  hero3XL900Italic: {
    color: 'heading',
    fontSize: ['XL', 'XL', '3XL'],
    fontWeight: 'strongest',
    fontStyle: 'italic',
    lineHeight: 'default',
    marginTop: 0,
    marginBottom: 6,
  },
  hero4XL700: {
    color: 'heading',
    fontSize: ['XL', 'XL', '4XL'],
    fontWeight: 'stronger',
    lineHeight: 'default',
    marginTop: 0,
    marginBottom: 6,
  },
  hero4XL900: {
    color: 'heading',
    fontSize: ['XL', 'XL', '4XL'],
    fontWeight: 'strongest',
    lineHeight: 'default',
    marginTop: 0,
    marginBottom: 6,
  },
  hero4XL900Italic: {
    color: 'heading',
    fontSize: ['XL', 'XL', '4XL'],
    fontWeight: 'strongest',
    fontStyle: 'italic',
    lineHeight: 'default',
    marginTop: 0,
    marginBottom: 6,
  },
  hero5XL700: {
    color: 'heading',
    fontSize: ['XL', 'XL', '5XL'],
    fontWeight: 'stronger',
    lineHeight: 'default',
    marginTop: 0,
    marginBottom: 6,
  },
  hero5XL900: {
    color: 'heading',
    fontSize: ['XL', 'XL', '2XL', '4XL', '5XL'],
    fontWeight: 'strongest',
    lineHeight: 'default',
    marginTop: 0,
    marginBottom: 6,
  },
  hero5XL900Italic: {
    color: 'heading',
    fontSize: ['XL', 'XL', '5XL'],
    fontWeight: 'strongest',
    fontStyle: 'italic',
    lineHeight: 'default',
    marginTop: 0,
    marginBottom: 6,
  },
};

export const headingStyles = {
  ...bodyHeadingStyles,
  ...heroHeadingStyles,
};

export type HeadingVariantsType = BodyHeadingVariantsType | HeroHeadingVariantsType;

export const headingVariants = Object.keys({ ...bodyHeadingStyles, ...heroHeadingStyles }) as HeadingVariantsType[];

export const heading: Heading = {
  ...headingStyles,
  products: {
    chart: headingStyles,
    chartblog: headingStyles,
    co: headingStyles,
    edu: headingStyles,
    scale: headingStyles,
    spark: headingStyles,
  },
};

export default heading;
